import { useCallback, useReducer } from 'react'

import type { Placement } from '@floating-ui/react'
import { clsx } from 'clsx'
import { HexColorInput, HexColorPicker } from 'react-colorful'

import { debounce } from '@bettermode/common/debounce'

import { FormControl } from '../FormControl/FormControl.js'
import { useControllableState } from '../hooks/useControllableState.js'
import { inputStyles } from '../Input/index.js'
import { Popover } from '../Popover/Popover.js'
import { Tooltip } from '../Tooltip/index.js'
import { ColorCircle } from './ColorCircle.js'
import { ColorCircleSize } from './types.js'

export type ColorPickerProps = {
  /**
   * A 7-character string specifying an RGB color in hexadecimal format
   */
  defaultColor?: string
  color?: string
  onChange?: (newColor: string) => void
  tooltip?: string
  variants?: string[]
  size?: ColorCircleSize
  invalid?: boolean
  disabled?: boolean
  placement?: Placement
  arrow?: boolean
  loading?: boolean
  loadingLabel?: string
  shouldDebounce?: boolean
  actionButton?: React.ReactNode
}

export const ColorPicker = ({
  color: colorProp,
  defaultColor,
  onChange: onChangeProp,
  tooltip,
  size = 'md',
  variants = [],
  invalid = false,
  disabled = false,
  placement = 'bottom-start',
  arrow = false,
  loading,
  loadingLabel,
  shouldDebounce = true,
  actionButton,
}: ColorPickerProps) => {
  const [show, toggleShow] = useReducer(state => !state, false)

  const debouncedOnChange = useCallback(
    debounce((inputValue: string) => {
      onChangeProp?.(inputValue)
    }, 300),
    [onChangeProp],
  )

  const [color, setColor] = useControllableState({
    prop: colorProp,
    defaultProp: defaultColor,
    onChange: newColor => {
      if (shouldDebounce) {
        debouncedOnChange(newColor)
        return
      }

      onChangeProp?.(newColor)
    },
  })

  return (
    <Popover
      placement={placement}
      activator={
        <Tooltip>
          <Tooltip.Trigger asChild>
            <ColorCircle
              size={size}
              color={color}
              invalid={invalid}
              disabled={disabled}
              onClick={toggleShow}
            />
          </Tooltip.Trigger>
          <Tooltip.Panel>{tooltip}</Tooltip.Panel>
        </Tooltip>
      }
      open={show}
      onClose={toggleShow}
      autofocusTarget="none"
      arrow={arrow}
      wrapperClassName="shrink-0 flex items-center"
    >
      <div className="p-3 flex flex-col gap-3">
        <HexColorPicker color={color} onChange={setColor} />
        <FormControl.Layout name="" label="Hex">
          <HexColorInput
            prefixed
            color={color}
            onChange={setColor}
            className={clsx([
              inputStyles({
                size: 'compact',
                fullWidth: false,
              }),
              'w-[200px]',
            ])}
            autoFocus
            onFocus={e => e.currentTarget.select()}
          />
          {actionButton}
        </FormControl.Layout>
        {variants.length > 0 && (
          <div className="flex gap-2 flex-wrap w-[200px]">
            {variants.map(presetColor => (
              <button
                type="button"
                key={presetColor}
                className="w-5 h-5 rounded-base border"
                style={{ background: presetColor }}
                onClick={() => setColor(presetColor)}
                aria-label={presetColor}
              />
            ))}
          </div>
        )}
        {loading && (
          <div className="text-sm text-content-subdued">
            {loadingLabel || 'Loading...'}
          </div>
        )}
      </div>
    </Popover>
  )
}
